import html2pdf from "html2pdf.js";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const A4 = { w: 210, h: 297 };

function stampHeaderFooter(pdf, { pageNumbers, headerText }) {
  const total = pdf.internal.getNumberOfPages();
  const w = pdf.internal.pageSize.getWidth();
  const h = pdf.internal.pageSize.getHeight();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    if (headerText && i > 1) pdf.text(headerText, 12, 8);
    if (pageNumbers) pdf.text(`Pag. ${i} / ${total}`, w - 28, h - 7);
  }
}

// Render each logical page element (marked with [data-pdf-page]) to exactly one
// A4 page. This avoids the blank pages html2pdf produces from rounding overflow.
async function exportByPages(pageEls, filename, opts) {
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  let first = true;

  for (const el of pageEls) {
    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    });
    const pxPerMm = canvas.width / A4.w;
    const pageHpx = Math.floor(A4.h * pxPerMm);
    const totalHpx = canvas.height;

    if (totalHpx <= pageHpx + 2) {
      if (!first) pdf.addPage();
      first = false;
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.98), "JPEG", 0, 0, A4.w, totalHpx / pxPerMm);
    } else {
      // Element taller than one page: slice it across multiple A4 pages.
      let offset = 0;
      while (offset < totalHpx - 1) {
        const sliceHpx = Math.min(pageHpx, totalHpx - offset);
        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = sliceHpx;
        const ctx = slice.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(canvas, 0, offset, canvas.width, sliceHpx, 0, 0, canvas.width, sliceHpx);
        if (!first) pdf.addPage();
        first = false;
        pdf.addImage(slice.toDataURL("image/jpeg", 0.98), "JPEG", 0, 0, A4.w, sliceHpx / pxPerMm);
        offset += sliceHpx;
      }
    }
  }

  if (opts.pageNumbers || opts.headerText) stampHeaderFooter(pdf, opts);
  pdf.save(filename);
}

// Export a DOM element to a downloadable A4 PDF.
// opts: { pageNumbers?: boolean, headerText?: string }
export async function exportElementToPdf(element, filename = "documento.pdf", opts = {}) {
  const { pageNumbers = false, headerText = "" } = opts;
  const pageEls = element && element.querySelectorAll ? element.querySelectorAll("[data-pdf-page]") : [];

  if (pageEls && pageEls.length) {
    return exportByPages(Array.from(pageEls), filename, { pageNumbers, headerText });
  }

  // Fallback: let html2pdf auto-paginate the whole element.
  const worker = html2pdf()
    .set({
      margin: 0,
      filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] },
    })
    .from(element);

  if (!pageNumbers && !headerText) {
    return worker.save();
  }

  return worker
    .toPdf()
    .get("pdf")
    .then((pdf) => stampHeaderFooter(pdf, { pageNumbers, headerText }))
    .save();
}
